/**
 * Base HTTP client for the socialiq backends (socialiq-sql / socialiq-bqv).
 *
 * Auth: every request carries the signed-in user's Firebase ID token as
 * `Authorization: Bearer <token>`. The backend verifies it against Google's public keys and
 * derives the audit actor from the token's verified email — so there is no shared API key in the
 * bundle, and the actor cannot be spoofed by a header. Firebase auto-refreshes the token near
 * expiry; on a backend 401 we force one refresh and retry once, and if it still fails we sign the
 * user out so the app falls back to the login screen.
 *
 * With Vite's dev proxy (vite.config.ts `server.proxy`), /api/* is forwarded to the local backend,
 * so VITE_API_BASE can be left empty. In production the Firebase Hosting rewrites route
 * /api/v2/sql|bqv/** to the matching Cloud Run service.
 */

import { auth, currentIdToken, signOutUser } from "../auth/firebase";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const doFetch = async (forceRefresh: boolean): Promise<Response> => {
    const token = await currentIdToken(forceRefresh);
    return fetch(`${BASE}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let res = await doFetch(false);
  // A 401 usually means the cached token just expired — force a fresh one and retry once.
  if (res.status === 401 && auth.currentUser) {
    res = await doFetch(true);
    // Still rejected with a fresh token → the session is genuinely invalid: sign out so the
    // app returns to the login screen, then surface the error to the caller.
    if (res.status === 401) {
      await signOutUser().catch(() => { /* ignore — we're already erroring out */ });
    }
  }

  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = res.statusText;
    }
    // Extract a human message. The v2 services (sql/bqv/content/ai) use the envelope
    // { error: { type, message } }; FastAPI's own errors use { detail }; socialiq-launcher's brand
    // endpoints answer { ok: false, error: "a sentence" }. Prefer the v2 envelope, then the launcher's
    // string, then `detail`, then the raw JSON so nothing is ever silently blank.
    //
    // The launcher case is not hypothetical: without it, a refused draft or a failed chat turn showed
    // the operator `{"ok":false,"error":"the model could not draft this plan: ..."}` — the reason was
    // right there and rendered as JSON, which reads as a crash rather than as an explanation.
    const envelopeMsg =
      typeof detail === "object" && detail !== null
        ? (((detail as { error?: { message?: string } }).error?.message) ??
           (typeof (detail as { error?: unknown }).error === "string"
             ? ((detail as { error: string }).error)
             : undefined) ??
           ((detail as Record<string, unknown>).detail as string | undefined))
        : undefined;
    // A 422 validation_error carries the actual reason per field in error.details
    // ([{ field, message }]) under a generic "request failed validation" message — append them, or
    // the operator sees that something failed but never which field or why.
    const fieldDetails =
      typeof detail === "object" && detail !== null
        ? (detail as { error?: { details?: unknown } }).error?.details
        : undefined;
    const fieldMsg = Array.isArray(fieldDetails)
      ? fieldDetails
          .map(d => {
            const { field, message } = (d ?? {}) as { field?: string; message?: string };
            return field ? `${field}: ${message ?? "invalid"}` : (message ?? "");
          })
          .filter(Boolean)
          .join("; ")
      : "";
    const baseMsg =
      envelopeMsg ??
      (typeof detail === "object" && detail !== null ? JSON.stringify(detail) : String(detail));
    const msg = fieldMsg ? `${baseMsg} — ${fieldMsg}` : baseMsg;
    throw new ApiError(res.status, detail, msg);
  }

  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const api = {
  get:    <T>(path: string)                 => request<T>("GET",    path),
  post:   <T>(path: string, body?: unknown) => request<T>("POST",   path, body),
  put:    <T>(path: string, body: unknown)  => request<T>("PUT",    path, body),
  // PATCH is a partial update, which is exactly what a bounded spec edit is: one path, one value,
  // leaving the rest of the document alone. PUT would imply the client is sending the whole thing.
  patch:  <T>(path: string, body: unknown)  => request<T>("PATCH",  path, body),
  delete: <T>(path: string)                 => request<T>("DELETE", path),
};

// Fetches a non-JSON body (e.g. streamed audio bytes) carrying the same Firebase auth header as
// `api.*` — a plain <img>/<audio> `src` can't attach one, and these backends have no public/unauthed
// object URLs (the source buckets are private; see socialiq_content/core/gcs.py:download_bytes).
export async function getBlob(path: string): Promise<Blob> {
  const token = await currentIdToken(false);
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, undefined, res.statusText);
  return res.blob();
}
